import React, { useState, useMemo, useRef, DragEvent, ChangeEvent, useEffect } from 'react';
import * as XLSX from 'xlsx';
import './lms-consumption-dashboard.style.scss';
import { getDashboardCsvFile } from '../../services/ApiActions/dashboardAct';
// -----------------------------------------------------------------------------
// TypeScript Interfaces
// -----------------------------------------------------------------------------
interface ContentItemValue {
  [key: string]: 'Yes' | 'No' | 'NA';
}

interface WeeklyEntry {
  pct: number | null;
  quiz: 'Pass' | 'Fail' | 'Not Appeared' | null;
}

interface Employee {
  emp_code: string;
  name: string;
  email: string;
  dept: string;
  manager: string;
  location: string;
  // One entry per detected "real" week (a week that has its own consumption %
  // and/or quiz column in the source file). Index 0 = Week 1, index 1 = Week 2, etc.
  weekly: WeeklyEntry[];
  content: ContentItemValue;
}

interface DeptStat {
  yes: number;
  total: number;
  pct: number;
}

interface DeptWeekQuiz {
  pass: number;
  fail: number;
  na: number;
}

interface Department {
  dept: string;
  count: number;
  weekly_avg: number[]; // avg consumption % per real week
  weekly_quiz: DeptWeekQuiz[]; // pass/fail/na counts per real week
  overall_avg: number;
  quiz_pass: number;
  quiz_fail: number;
  quiz_na: number;
}

interface ContentStat {
  col: string;
  title: string;
  date: string;
  week: number;
  yes: number;
  total: number;
  pct: number;
  dept_stats: { [key: string]: DeptStat };
}

interface ContentInfo {
  col: string;
  title: string;
  date: string;
  week: number;
}

interface WeekOverview {
  week: number;
  label: string;
  dateRange: string;
  hasEmployeeData: boolean; // true when this week has real per-employee pct/quiz columns
  avg_pct: number;
  content_count: number;
}

interface Summary {
  total_emp: number;
  total_dept: number;
  weeks: WeekOverview[]; // every detected week (real weeks + content-only weeks)
  monthly_overall: number;
  top_dept: string;
  top_dept_pct: number;
  bottom_dept: string;
  latest_content: string;
  latest_content_pct: number;
  latest_content_date: string;
}

interface DashboardData {
  content_stats: ContentStat[];
  dept_overall: Department[];
  emp_data: Employee[];
  content_info: ContentInfo[];
  summary: Summary;
  week_count: number; // number of "real" weeks (weeks with their own pct/quiz column)
  _debug: {
    weekCols: string;
    hdrRow: number;
    smp: string;
  };
}

const FILENAME_TO_MOD_MAP: Record<string, string> = {
  'ask-me-anything': 'Ask Me Anything',
  'best_call': 'Best Calls',
  'informed_investor': 'Informed Investor',
  'knowledgebase': 'Knowledge Base',
  'marketing': 'Marketing',
  'news_letter': 'Newsletter',
  'research': 'Research',
  'scenario': 'Scenarios',
  'whats_trending': 'Whats Trending',
  'consumption': 'LMS Consumption'
};

const weeklyQuizLabel = (weekIndex: number): string => {
  // 0 -> I, 1 -> II, otherwise just the number
  if (weekIndex === 0) return 'I';
  if (weekIndex === 1) return 'II';
  return String(weekIndex + 1);
};

// Quiz data/columns are never shown beyond Week 3, no matter how many
// consumption-% weeks exist in the uploaded file. Consumption % itself
// is unaffected and still shows for every detected week.
const MAX_QUIZ_WEEKS = 3;

const LmsConsumptionDashboard: React.FC = () => {
  // -----------------------------------------------------------------------------
  // States & Refs
  // -----------------------------------------------------------------------------
  const [data, setData] = useState<DashboardData | null>(null);
  const [activeTab, setActiveTab] = useState<string>('ov');
  
  // Tab-specific interactive states
  const [deptSearch, setDeptSearch] = useState<string>('');
  const [selectedDeptDrill, setSelectedDeptDrill] = useState<string | null>(null);
  const [contentWeekFilter, setContentWeekFilter] = useState<number>(0);
  const [empSearchQuery, setEmpSearchQuery] = useState<string>('');
  const [empDeptFilter, setEmpDeptFilter] = useState<string>('');
  const [selectedEmpEmail, setSelectedEmpEmail] = useState<string | null>(null);
  
  // Upload and drag-and-drop flags
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [uploadStatus, setUploadStatus] = useState<{ text: string; className: string }>({ text: '', className: '' });
  const [diagnosticRows, setDiagnosticsRows] = useState<{ r0: string; r1: string; r2: string } | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const drilldownRef = useRef<HTMLDivElement>(null);

  const [toastMessage, setToastMessage] = useState<string>('Fetching Data from server');
  const [toastType, setToastType] = useState<'success' | 'error' | 'info'>('info');
  const [toastShow, setToastShow] = useState<boolean>(false);
  const toastTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const userString = localStorage?.getItem('user');

  const user = userString ? JSON.parse(userString) : undefined;

  // Month -> spoonfeed-bucket file URL, as returned by the API. e.g.
  // { "July": "https://spoonfeed-bucket.../..._June_2026_....xlsx", "June": "https://..." }
  const [monthlyFileMap, setMonthlyFileMap] = useState<Record<string, string>>({});
  const [selectedMonth, setSelectedMonth] = useState<string>('');
  const monthOptions = useMemo(() => Object.keys(monthlyFileMap), [monthlyFileMap]);

  useEffect(()=> {
    fetchDashboardUrl()
  }, [])

  const triggerToast = (msg: string, type: 'success' | 'error' | 'info' = 'info') => {
    setToastMessage(msg);
    setToastType(type);
    setToastShow(true);
    if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    toastTimeoutRef.current = setTimeout(() => {
      setToastShow(false);
    }, 3500);
  };

  // Static running date string
  const headerDateStr = useMemo(() => {
    return new Date().toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
  }, []);

  // -----------------------------------------------------------------------------
  // Utility & Styling Helper Functions
  // -----------------------------------------------------------------------------
  const getPctColor = (p: number | null | undefined): string => {
    if (p === null || p === undefined || isNaN(p)) return '#808080';
    if (p >= 80) return '#27ae60';
    if (p >= 60) return '#f39c12';
    return '#e74c3c';
  };

  const getRankClass = (index: number): string => {
    return index === 0 ? 'r1' : index === 1 ? 'r2' : index === 2 ? 'r3' : 'rn';
  };

  const calculateOverallPct = (e: Employee): number | null => {
    const v: number[] = [];
    e.weekly.forEach(wk => {
      if (wk.pct !== null && wk.pct !== undefined) v.push(wk.pct);
    });
    if (!v.length) return null;
    let sum = 0;
    for (let i = 0; i < v.length; i++) sum += v[i];
    return Math.round((sum / v.length) * 10) / 10;
  };

  // -----------------------------------------------------------------------------
  // Component Parsers (Converted safely into React views)
  // -----------------------------------------------------------------------------
  const parseUploadedRows = (rows: any[][]): DashboardData => {
    if (rows.length < 3) throw new Error('File too short (need 3+ rows)');
    
    const sv = (v: any): string => (v === null || v === undefined) ? '' : String(v).trim();

    let hdrRowIdx = -1;
    let titlesRowIdx = -1;

    for (let ri = 0; ri < Math.min(6, rows.length); ri++) {
      const row = rows[ri] || [];
      let hasName = false;
      let hasDept = false;
      for (let ci = 0; ci < row.length; ci++) {
        const s = sv(row[ci]).toLowerCase();
        if (s.indexOf('name') >= 0 && s.length < 30) hasName = true;
        if (s.indexOf('department') >= 0) hasDept = true;
      }
      if (hasName && hasDept) {
        hdrRowIdx = ri;
        titlesRowIdx = ri - 1 < 0 ? -1 : ri - 1;
        break;
      }
    }

    if (hdrRowIdx < 0) throw new Error('Cannot find header row (need Name + Department columns in first 6 rows).');

    const hdrs = rows[hdrRowIdx];
    const titles = titlesRowIdx >= 0 ? rows[titlesRowIdx] : [];
    const dataStart = hdrRowIdx + 1;

    const fc = (...terms: string[]): number => {
      for (let i = 0; i < hdrs.length; i++) {
        const hs = sv(hdrs[i]).toLowerCase();
        let ok = true;
        for (let j = 0; j < terms.length; j++) {
          if (hs.indexOf(terms[j]) < 0) { ok = false; break; }
        }
        if (ok) return i;
      }
      return -1;
    };

    const srI = fc('sr');
    const empI = fc('emp');
    const nameI = fc('name');
    const emailI = fc('email');
    const deptI = fc('department');
    
    let mgrI = -1;
    for (let i = 0; i < hdrs.length; i++) {
      const hs = sv(hdrs[i]).toLowerCase();
      if (hs.indexOf('reporting') >= 0 || (hs.indexOf('manager') >= 0 && hs.indexOf('account') < 0)) {
        mgrI = i;
        break;
      }
    }
    const locI = fc('location');

    if (nameI < 0) throw new Error('Cannot find Name column in row ' + (hdrRowIdx + 1));
    if (deptI < 0) throw new Error('Cannot find Department column in row ' + (hdrRowIdx + 1));

    // ---------------------------------------------------------------------
    // Dynamically detect ALL week columns (not just week 1 / week 2).
    // Every "consumption %" column found becomes its own week, paired with
    // the nearest quiz column (falling back to pctCol + 1 if unmatched).
    // ---------------------------------------------------------------------
    const pctCols: number[] = [];
    const quizCols: number[] = [];

    for (let i = 0; i < hdrs.length; i++) {
      const hs = sv(hdrs[i]).toLowerCase().replace(/\s+/g, ' ');
      if (hs.indexOf('consumption') >= 0 && (hs.indexOf('%') >= 0 || hs.indexOf('week') >= 0 || hs === 'weekly consumption (%)')) {
        pctCols.push(i);
      }
      if (hs.indexOf('quiz') >= 0) {
        quizCols.push(i);
      }
    }

    pctCols.sort((a, b) => a - b);
    quizCols.sort((a, b) => a - b);

    const weekCols: { pctCol: number; quizCol: number }[] = pctCols.map((pCol, idx) => {
      const qCol = quizCols[idx] !== undefined ? quizCols[idx] : pCol + 1;
      return { pctCol: pCol, quizCol: qCol };
    });
    const weekCount = weekCols.length;

    const special: { [key: number]: boolean } = {};
    [srI, empI, nameI, emailI, deptI, mgrI, locI].forEach(i => { if (i >= 0) special[i] = true; });
    weekCols.forEach(w => { special[w.pctCol] = true; special[w.quizCol] = true; });

    const exDt = (n: number): string => {
      const d = new Date(Math.round((n - 25569) * 86400 * 1000));
      return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', timeZone: 'UTC' });
    };

    // Content columns before the first week's pct col belong to week 1,
    // content between week N and week N+1's pct col belongs to week N+1,
    // and anything after the LAST detected pct col is bucketed into an
    // extra "content only" week (weekCount + 1) which has no per-employee
    // pct/quiz data of its own (its % is derived from content averages).
    const wkOf = (i: number): number => {
      for (let w = 0; w < weekCols.length; w++) {
        if (i < weekCols[w].pctCol) return w + 1;
      }
      return weekCols.length + 1;
    };

    const ccols: ContentInfo[] = [];
    for (let i = 0; i < titles.length; i++) {
      if (i < 7 || special[i]) continue;
      const t = sv(titles[i]);
      if (!t || t.toLowerCase().indexOf('week') >= 0 || t.toLowerCase().indexOf('quiz') >= 0 || t === 'nan') continue;
      
      const dv = hdrs[i];
      let ds = '';
      if (typeof dv === 'number' && dv > 40000) ds = exDt(dv);
      ccols.push({ col: 'c' + i, title: t, date: ds, week: wkOf(i) });
    }

    if (!ccols.length) throw new Error('No content columns detected');

    const nQ = (v: any): 'Pass' | 'Fail' | 'Not Appeared' | null => {
      const s2 = sv(v);
      if (!s2 || s2 === 'null') return null;
      const sl = s2.toLowerCase();
      if (sl === 'pass') return 'Pass';
      if (sl === 'fail') return 'Fail';
      if (sl.indexOf('not') >= 0 || sl.indexOf('appear') >= 0) return 'Not Appeared';
      return null;
    };

    const nP = (v: any): number | null => {
      if (v === null || v === undefined) return null;
      const s2 = sv(v);
      if (s2 === 'NA' || s2 === 'nan' || s2 === '') return null;
      const n = parseFloat(s2);
      if (isNaN(n)) return null;
      return Math.round((n <= 1.0 ? n * 100 : n) * 10) / 10;
    };

    const empData: Employee[] = [];
    for (let ri = dataStart; ri < rows.length; ri++) {
      const row = rows[ri];
      if (!row) continue;
      const srRaw = srI >= 0 ? row[srI] : row[0];
      if (!srRaw || isNaN(parseInt(srRaw))) continue;
      const nm = sv(row[nameI]);
      if (!nm || nm.toLowerCase().indexOf('name') >= 0) continue;

      const content: ContentItemValue = {};
      for (let i = 0; i < ccols.length; i++) {
        const cc = ccols[i];
        const rawColIndex = parseInt(cc.col.replace('c', ''), 10);
        const v = sv(row[rawColIndex]);
        content[cc.col] = (v === 'Yes' || v === 'No') ? v : 'NA';
      }

      const weekly: WeeklyEntry[] = weekCols.map(w => ({
        pct: nP(row[w.pctCol]),
        quiz: nQ(row[w.quizCol])
      }));

      empData.push({
        emp_code: sv(row[empI]) || '-',
        name: nm,
        email: sv(row[emailI]) || '',
        dept: sv(row[deptI]) || 'Unknown',
        manager: mgrI >= 0 ? sv(row[mgrI]) : '',
        location: locI >= 0 ? sv(row[locI]) : '',
        weekly: weekly,
        content: content
      });
    }

    if (!empData.length) throw new Error('No employee rows found');

    const dm: { [key: string]: Employee[] } = {};
    empData.forEach(e => {
      if (!dm[e.dept]) dm[e.dept] = [];
      dm[e.dept].push(e);
    });

    const avg = (arr: number[]): number => {
      if (!arr.length) return 0;
      let s = 0;
      for (let i = 0; i < arr.length; i++) s += arr[i];
      return Math.round((s / arr.length) * 10) / 10;
    };

    const deptOverall: Department[] = [];
    const dkeys = Object.keys(dm);
    dkeys.forEach(dept => {
      const emps = dm[dept];
      const weeklyPctArrs: number[][] = Array.from({ length: weekCount }, () => []);
      const weeklyQuizCounts: DeptWeekQuiz[] = Array.from({ length: weekCount }, () => ({ pass: 0, fail: 0, na: 0 }));
      let pass = 0, fail = 0, na = 0;

      emps.forEach(e => {
        e.weekly.forEach((wk, wi) => {
          if (wk.pct !== null) weeklyPctArrs[wi].push(wk.pct);
          if (wk.quiz === 'Pass') { weeklyQuizCounts[wi].pass++; pass++; }
          else if (wk.quiz === 'Fail') { weeklyQuizCounts[wi].fail++; fail++; }
          else if (wk.quiz === 'Not Appeared') { weeklyQuizCounts[wi].na++; na++; }
        });
      });

      const weeklyAvgArr = weeklyPctArrs.map(arr => avg(arr));
      const overallAvg = weeklyAvgArr.length
        ? Math.round((weeklyAvgArr.reduce((s, v) => s + v, 0) / weeklyAvgArr.length) * 10) / 10
        : 0;

      deptOverall.push({
        dept: dept,
        count: emps.length,
        weekly_avg: weeklyAvgArr,
        weekly_quiz: weeklyQuizCounts,
        overall_avg: overallAvg,
        quiz_pass: pass,
        quiz_fail: fail,
        quiz_na: na
      });
    });

    deptOverall.sort((a, b) => b.overall_avg - a.overall_avg);

    const cs: ContentStat[] = [];
    ccols.forEach(cc => {
      const ds2: { [key: string]: DeptStat } = {};
      dkeys.forEach(dept => {
        const emps = dm[dept];
        const valid: string[] = [];
        let yes = 0;
        emps.forEach(e => {
          const v2 = e.content[cc.col];
          if (v2 !== 'NA') {
            valid.push(v2);
            if (v2 === 'Yes') yes++;
          }
        });
        ds2[dept] = {
          yes: yes,
          total: valid.length,
          pct: valid.length ? Math.round((yes / valid.length) * 1000) / 10 : 0
        };
      });

      const av: string[] = [];
      let ay = 0;
      empData.forEach(e => {
        const v2 = e.content[cc.col];
        if (v2 !== 'NA') {
          av.push(v2);
          if (v2 === 'Yes') ay++;
        }
      });

      cs.push({
        col: cc.col,
        title: cc.title,
        date: cc.date,
        week: cc.week,
        yes: ay,
        total: av.length,
        pct: av.length ? Math.round((ay / av.length) * 1000) / 10 : 0,
        dept_stats: ds2
      });
    });

    // ---------------------------------------------------------------------
    // Build a dynamic per-week overview: every real week (with employee
    // pct/quiz data) plus any "content only" trailing weeks.
    // ---------------------------------------------------------------------
    const maxContentWeek = ccols.reduce((m, c) => Math.max(m, c.week), 0);
    const totalWeeksForOverview = Math.max(weekCount, maxContentWeek);

    const weeks: WeekOverview[] = [];
    for (let w = 1; w <= totalWeeksForOverview; w++) {
      const isReal = w <= weekCount;
      let avgPct = 0;
      if (isReal) {
        const vals: number[] = [];
        empData.forEach(e => {
          const wk = e.weekly[w - 1];
          if (wk && wk.pct !== null) vals.push(wk.pct);
        });
        avgPct = avg(vals);
      } else {
        const weekContent = cs.filter(c => c.week === w);
        avgPct = weekContent.length
          ? Math.round((weekContent.reduce((s, c) => s + c.pct, 0) / weekContent.length) * 10) / 10
          : 0;
      }

      const weekContentItems = ccols.filter(c => c.week === w);
      const dates = weekContentItems.map(c => c.date).filter(Boolean);
      let dateRange = '';
      if (dates.length === 1) dateRange = dates[0];
      else if (dates.length > 1) dateRange = `${dates[0]} - ${dates[dates.length - 1]}`;

      weeks.push({
        week: w,
        label: `Week ${w}`,
        dateRange,
        hasEmployeeData: isReal,
        avg_pct: avgPct,
        content_count: weekContentItems.length
      });
    }

    const monthlyOverall = weeks.length
      ? Math.round((weeks.reduce((s, w) => s + w.avg_pct, 0) / weeks.length) * 10) / 10
      : 0;

    const lc = cs[cs.length - 1] || {};
    const smp = empData[0] || { name: '', dept: '', weekly: [] as WeeklyEntry[] };

    return {
      content_stats: cs,
      dept_overall: deptOverall,
      emp_data: empData,
      content_info: ccols,
      week_count: weekCount,
      _debug: {
        weekCols: JSON.stringify(weekCols),
        hdrRow: hdrRowIdx,
        smp: (smp.name || '') + '|' + (smp.dept || '') + '|weeks:' + JSON.stringify((smp.weekly || []).map(w => w.quiz))
      },
      summary: {
        total_emp: empData.length,
        total_dept: dkeys.length,
        weeks: weeks,
        monthly_overall: monthlyOverall,
        top_dept: deptOverall[0] ? deptOverall[0].dept : '',
        top_dept_pct: deptOverall[0] ? deptOverall[0].overall_avg : 0,
        bottom_dept: deptOverall[deptOverall.length - 1] ? deptOverall[deptOverall.length - 1].dept : '',
        latest_content: lc.title || '',
        latest_content_pct: lc.pct || 0,
        latest_content_date: lc.date || ''
      }
    };
  };

  const detectModuleFromFilename = (fn: string): string | null => {
    const f = fn.toLowerCase();
    for (const [k, v] of Object.entries(FILENAME_TO_MOD_MAP)) {
      if (f.includes(k)) return v;
    }
    return null;
  };

   const fetchDashboardUrl = async () => {
    try {
      // setIsFetching(true);
      triggerToast('Fetching data from server…', 'info');

      // 1. Get the { month: url } map from the API, e.g.
      //    { "July": "https://spoonfeed-bucket.../xyz.xlsx", "June": "https://..." }
      const res: any = await getDashboardCsvFile();
      const monthMap: Record<string, string> = res?.data;

      if (!monthMap || typeof monthMap !== 'object' || !Object.keys(monthMap).length) {
        triggerToast('No file URL returned from server', 'error');
        return;
      }

      setMonthlyFileMap(monthMap);

      // 2. Default to the month at the 0th index and load its file
      const defaultMonth = Object.keys(monthMap)[0];
      setSelectedMonth(defaultMonth);
      await loadMonthFile(defaultMonth, monthMap[defaultMonth]);

    } catch (error) {
      console.error('fetchDashboardUrl error:', error);
      triggerToast('Error fetching dashboard data', 'error');
    } finally {
      // setIsFetching(false);
    }
    };

  // Downloads the spoonfeed-bucket file for a given month and pushes it
  // through the existing processFile pipeline — no duplication of logic.
  const loadMonthFile = async (month: string, url: string) => {
    try {
      if (!url) {
        triggerToast(`No file URL found for ${month}`, 'error');
        return;
      }

      triggerToast(`Fetching ${month} data…`, 'info');

      const filename = url.split('/').pop() || '';
      const detectedMod = detectModuleFromFilename(filename);

      if (!detectedMod) {
        triggerToast(`Could not detect module from filename: ${filename}`, 'error');
        return;
      }

      const fetchRes = await fetch(url);
      if (!fetchRes.ok) {
        triggerToast(`Failed to download file: ${fetchRes.statusText}`, 'error');
        return;
      }
      const arrayBuffer = await fetchRes.arrayBuffer();

      const file = new File([arrayBuffer], filename, {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });

      processFile(file);
    } catch (error) {
      console.error('loadMonthFile error:', error);
      triggerToast(`Error fetching ${month} dashboard data`, 'error');
    }
  };

  // Fired when the person picks a different month from the header dropdown
  const handleMonthChange = (e: ChangeEvent<HTMLSelectElement>) => {
    const month = e.target.value;
    setSelectedMonth(month);
    const url = monthlyFileMap[month];
    if (url) loadMonthFile(month, url);
  };

  const processFile = (file: File) => {
    if (!file) return;
    setUploadStatus({ text: `Processing ${file.name}...`, className: '' });
    
    const reader = new FileReader();
    reader.onload = (e: ProgressEvent<FileReader>) => {
      try {
        if (!e.target?.result) return;
        const arrayBuffer = e.target.result as ArrayBuffer;
        const workbook = XLSX.read(arrayBuffer, { type: 'array' });
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: null }) as any[][];
        
        const formatRowString = (row: any[] | undefined): string => {
          const out: string[] = [];
          (row || []).forEach((cell, idx) => {
            if (cell !== null) out.push(`[${idx}]=${JSON.stringify(cell)}`);
          });
          return out.join(' | ');
        };

        setDiagnosticsRows({
          r0: formatRowString(rows[0]),
          r1: formatRowString(rows[1]),
          r2: formatRowString(rows[2]),
        });

        const parsedData = parseUploadedRows(rows);
        setData(parsedData);
        
        // Reset view scopes
        setDeptSearch('');
        setSelectedDeptDrill(null);
        setContentWeekFilter(0);
        setEmpSearchQuery('');
        setEmpDeptFilter('');
        setSelectedEmpEmail(null);

        const dbg = parsedData._debug || {};
        setUploadStatus({
          className: 'uok',
          text: `OK: ${parsedData.emp_data.length} employees | ${parsedData.summary.total_dept} depts | ${parsedData.content_stats.length} content | Weeks detected: ${parsedData.week_count} | hdrRow:${dbg.hdrRow} | ${dbg.smp}`
        });
      } catch (err) {
        setUploadStatus({ className: 'uerr', text: `Error: ${err.message}` });
        console.error(err);
      }
    };
    reader.readAsArrayBuffer(file);
  };

  // -----------------------------------------------------------------------------
  // Event Handlers
  // -----------------------------------------------------------------------------
  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      processFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      processFile(e.target.files[0]);
    }
  };

  // -----------------------------------------------------------------------------
  // Memoized Tab Compulations / Render Mappings
  // -----------------------------------------------------------------------------
  const uniqueDepartments = useMemo(() => {
    if (!data) return [];
    const depts = data.emp_data.map(e => e.dept);
    return Array.from(new Set(depts)).sort();
  }, [data]);

  const sortedLeaderboardEmployees = useMemo(() => {
    if (!data) return [];
    return data.emp_data
      .map(e => ({ employee: e, overall: calculateOverallPct(e) }))
      .filter(x => x.overall !== null)
      .sort((a, b) => (b.overall || 0) - (a.overall || 0));
  }, [data]);

  const filteredEmployeeSearchResults = useMemo(() => {
    if (!data) return [];
    const query = empSearchQuery.toLowerCase().trim();
    if (!query && !empDeptFilter) return [];
    
    return data.emp_data.filter(e => {
      const matchQuery = !query || 
        e.name.toLowerCase().includes(query) || 
        e.email.toLowerCase().includes(query) || 
        e.emp_code.toLowerCase().includes(query);
      const matchDept = !empDeptFilter || e.dept === empDeptFilter;
      return matchQuery && matchDept;
    }).slice(0, 20);
  }, [data, empSearchQuery, empDeptFilter]);

  const selectedEmployeeObject = useMemo(() => {
    if (!data || !selectedEmpEmail) return null;
    return data.emp_data.find(e => e.email === selectedEmpEmail) || null;
  }, [data, selectedEmpEmail]);

  // Per-week Pass/Fail/Not-Appeared aggregates (one entry per real week)
  const quizAggregates = useMemo(() => {
    if (!data) return [] as { pass: number; fail: number; na: number; total: number }[];
    const quizWeekCount = Math.min(data.week_count, MAX_QUIZ_WEEKS);
    const stats = Array.from({ length: quizWeekCount }, () => ({ pass: 0, fail: 0, na: 0, total: 0 }));
    data.emp_data.forEach(e => {
      e.weekly.forEach((wk, wi) => {
        if (wi >= quizWeekCount) return;
        if (wk.quiz) {
          stats[wi].total++;
          if (wk.quiz === 'Pass') stats[wi].pass++;
          else if (wk.quiz === 'Fail') stats[wi].fail++;
          else stats[wi].na++;
        }
      });
    });
    return stats;
  }, [data]);

  // Distinct list of week numbers that actually have content pushed (for the Content tab dropdown)
  const contentWeekOptions = useMemo(() => {
    if (!data) return [];
    const weekSet = new Set(data.content_info.map(c => c.week));
    return Array.from(weekSet).sort((a, b) => a - b);
  }, [data]);

  // -----------------------------------------------------------------------------
  // Sub-Render Fragments
  // -----------------------------------------------------------------------------
  const renderProgressBar = (pct: number | null | undefined) => {
    const v = pct || 0;
    return (
      <div className="pbw">
        <div className="pb" style={{ width: `${v}%`, backgroundColor: getPctColor(v)}} />
      </div>
    );
  };

  const renderQuizBadge = (status: string | null) => {
    if (!status || status === 'null') return <span className="bdg bn2">N/A</span>;
    if (status === 'Pass') return <span className="bdg bp">Pass</span>;
    if (status === 'Fail') return <span className="bdg bf">Fail</span>;
    return <span className="bdg bn2" style={{textAlign: 'center'}}>Not Appeared</span>;
  };

  const renderPctText = (p: number | null | undefined) => {
    if (p === null || p === undefined) return <span style={{ color: 'var(--tlight)' }}>&#8212;</span>;
    return <span style={{ fontWeight: 700, color: getPctColor(p) }}>{p}%</span>;
  };

  return (
    <>

      <div className="lms-dashboard-wrapper">
        {/* <div id="err-banner" style={{ display: !data ? 'block' : 'none' }}>
          DATA failed to load. Please head over to the "Upload Data" tab to feed a metrics file.
        </div> */}

        <div className="hdr">
          <div className="brand">
            <div className="bull">🐂</div>
            <div className="bn"><strong>equentis</strong><span>Making Equity Easy</span></div>
          </div>
          <span className="ht">LMS Consumption Dashboard — FY 2026-27</span>
          {monthOptions.length > 0 ? (
            <select
              className="hd"
              id="hdr-date"
              value={selectedMonth}
              onChange={handleMonthChange}
              style={{
                background: 'transparent',
                border: '1px solid rgba(255,255,255,0.35)',
                borderRadius: '6px',
                padding: '4px 10px',
                fontWeight: 900,
                cursor: 'pointer',
                color: 'white',
                font: 'inherit'
              }}
            >
              {monthOptions.map(m => <option value={m} key={m}>{m}</option>)}
            </select>
          ) : (
            <span className="hd" id="hdr-date">{headerDateStr}</span>
          )}
        </div>

        <div className="tabs">
          <button className={`tab ${activeTab === 'ov' ? 'on' : ''}`} onClick={() => setActiveTab('ov')}>📊 Overview</button>
          <button className={`tab ${activeTab === 'lb' ? 'on' : ''}`} onClick={() => setActiveTab('lb')}>🏆 Leaderboard</button>
          <button className={`tab ${activeTab === 'dp' ? 'on' : ''}`} onClick={() => setActiveTab('dp')}>🏢 Departments</button>
          <button className={`tab ${activeTab === 'co' ? 'on' : ''}`} onClick={() => setActiveTab('co')}>📚 Content</button>
          <button className={`tab ${activeTab === 'em' ? 'on' : ''}`} onClick={() => setActiveTab('em')}>👤 My Tracker</button>
          {
            user?.email === 'pooja.shah@equentis.com' && 
          <button className={`tab ${activeTab === 'up' ? 'on' : ''}`} onClick={() => setActiveTab('up')}>📤 Upload Data</button>
          }
        </div>

        <div className="main">
          {data ? (
            <>
              {/* ── OVERVIEW PANEL ────────────────────────────────────────── */}
              <div className={`pnl ${activeTab === 'ov' ? 'on' : ''}`}>
                <div className="g5" style={{ gridTemplateColumns: `repeat(${3 + data.summary.weeks.length}, 1fr)` }}>
                  <div className="card">
                    <div className="ct">Total Employees</div>
                    <div className="cv">{data.summary.total_emp}</div>
                    <div className="cs">{data.summary.total_dept} departments</div>
                  </div>
                  {data.summary.weeks.map(w => (
                    <div className="card" key={w.week}>
                      <div className="ct">{w.label} Consumption</div>
                      <div className="cv">{w.avg_pct}%</div>
                      <div className="cs">Average across all employees</div>
                    </div>
                  ))}
                  <div className="card">
                    <div className="ct">Monthly Consumption</div>
                    <div className="cv">{data.summary.monthly_overall}%</div>
                    <div className="cs">Overall for the month</div>
                  </div>
                  <div className="card">
                    <div className="ct">Top Department</div>
                    <div className="cv acc" style={{ fontSize: data.summary.top_dept.length > 8 ? '16px' : '28px' }}>
                      {data.summary.top_dept.split(' ').slice(0, 3).join(' ')}
                    </div>
                    <div className="cs">{data.summary.top_dept} - {data.summary.top_dept_pct}%</div>
                  </div>
                  <div className="card">
                    <div className="ct">Latest Content</div>
                    <div className="cv">{data.summary.latest_content_pct}%</div>
                    <div className="cs">{data.summary.latest_content} ({data.summary.latest_content_date})</div>
                  </div>
                </div>

                <div className="g2">
                  <div className="card">
                    <div className="sh">Weekly Consumption Trend</div>
                    {data.summary.weeks.map((w, idx) => (
                      <div style={{ marginBottom: idx === data.summary.weeks.length - 1 ? 0 : '18px' }} key={w.week}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                          <span style={{ fontWeight: 600 }}>{w.label}{w.dateRange ? ` (${w.dateRange})` : ''}</span>
                          <span style={{ fontWeight: 800, color: getPctColor(w.avg_pct) }}>{w.avg_pct}%</span>
                        </div>
                        {renderProgressBar(w.avg_pct)}
                        <div style={{ fontSize: '11px', color: 'var(--tlight)', marginTop: '4px' }}>
                          {w.content_count} content piece(s) pushed
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="card">
                    <div className="sh">Latest Content Push</div>
                    {data.content_stats.length > 0 && (() => {
                      const lat = data.content_stats[data.content_stats.length - 1];
                      const lds = lat.dept_stats || {};
                      const ldk = Object.keys(lds).sort((a, b) => lds[b].pct - lds[a].pct);
                      return (
                        <>
                          <div style={{ marginBottom: '16px' }}>
                            <div style={{ fontSize: '15px', fontWeight: '800', marginBottom: '4px' }}>{lat.title}</div>
                            <div style={{ fontSize: '12px', color: 'var(--tlight)' }}>Pushed on {lat.date} &bull; {lat.yes} of {lat.total} consumed</div>
                            <div style={{ fontSize: '28px', fontWeight: 800, color: getPctColor(lat.pct), margin: '12px 0 8px' }}>{lat.pct}%</div>
                            {renderProgressBar(lat.pct)}
                          </div>
                          <div style={{ fontSize: '12px', fontWeight: 700, color: 'var(--tmid)', marginBottom: '8px', textTransform: 'uppercase' }}>Top 5 Depts</div>
                          {ldk.slice(0, 5).map(deptKey => (
                            <div className="dr" key={deptKey}>
                              <div className="dl" title={deptKey}>{deptKey}</div>
                              <div style={{ flex: 1 }}>{renderProgressBar(lds[deptKey].pct)}</div>
                              <div className="dv" style={{ color: getPctColor(lds[deptKey].pct) }}>{lds[deptKey].pct}%</div>
                            </div>
                          ))}
                        </>
                      );
                    })()}
                  </div>
                </div>

                <div className="g2">
                  <div className="card sy">
                    <div className="sh">Top Performing Departments</div>
                    {data.dept_overall.slice(0, 8).map(dep => (
                      <div className="att" style={{ borderLeftColor: getPctColor(dep.overall_avg) }} key={dep.dept}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                          <div style={{ fontWeight: 700 }}>{dep.dept}</div>
                          <div style={{ fontSize: '20px', fontWeight: 800, color: getPctColor(dep.overall_avg) }}>{dep.overall_avg}%</div>
                        </div>
                        {renderProgressBar(dep.overall_avg)}
                        <div style={{ fontSize: '11px', color: 'var(--tlight)', marginTop: '4px' }}>
                          {dep.count} employees &bull; {dep.weekly_avg.map((v, i) => `W${i + 1}: ${v}%`).join(' \u2022 ')}
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="card sy">
                    <div className="sh">Departments Needing Attention</div>
                    {[...data.dept_overall].sort((a, b) => a.overall_avg - b.overall_avg).slice(0, 8).map(dep => (
                      <div className="att" key={dep.dept}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div style={{ fontWeight: 700 }}>{dep.dept}</div>
                          <div style={{ fontSize: '20px', fontWeight: 800, color: 'var(--red)' }}>{dep.overall_avg}%</div>
                        </div>
                        {renderProgressBar(dep.overall_avg)}
                        <div style={{ fontSize: '11px', color: 'var(--tlight)', marginTop: '4px' }}>
                          {dep.count} employees &bull; {dep.weekly_avg.map((v, i) => `W${i + 1}: ${v}%`).join(' \u2022 ')}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="sh">Quiz Summary</div>
                {quizAggregates.map((qa, idx) => (
                  <div style={{ marginBottom: idx === quizAggregates.length - 1 ? '24px' : '10px' }} key={idx}>
                    <div style={{ fontSize: '12px', fontWeight: 700, color: 'var(--tmid)', textTransform: 'uppercase', letterSpacing: '.8px', marginBottom: '10px' }}>
                      Weekly Quiz {weeklyQuizLabel(idx)} &mdash; Week {idx + 1}
                    </div>
                    <div className="g3">
                      <div className="card" style={{ borderLeft: '4px solid #27ae60', background: '#e8f8f0' }}>
                        <div className="ct">Pass</div>
                        <div className="cv" style={{ color: '#27ae60' }}>{qa.pass}</div>
                        <div className="cs">{qa.total ? Math.round((qa.pass / qa.total) * 100) : 0}% of {qa.total} participants</div>
                      </div>
                      <div className="card" style={{ borderLeft: '4px solid #e74c3c', background: '#fdecea' }}>
                        <div className="ct">Fail</div>
                        <div className="cv" style={{ color: '#e74c3c' }}>{qa.fail}</div>
                        <div className="cs">{qa.total ? Math.round((qa.fail / qa.total) * 100) : 0}% of {qa.total} participants</div>
                      </div>
                      <div className="card" style={{ borderLeft: '4px solid #808080', background: '#f5f5f5' }}>
                        <div className="ct">Not Appeared</div>
                        <div className="cv" style={{ color: '#808080' }}>{qa.na}</div>
                        <div className="cs">{qa.total ? Math.round((qa.na / qa.total) * 100) : 0}% of {qa.total} participants</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* ── LEADERBOARD PANEL ─────────────────────────────────────── */}
              <div className={`pnl ${activeTab === 'lb' ? 'on' : ''}`}>
                <div className="g2">
                  <div>
                    <div className="sh">Top 20 Employees</div>
                    <div className="ssub">Ranked by average consumption %</div>
                    <div className="card" style={{ padding: 0 }}>
                      <div className="sy2" style={{ padding: '16px' }}>
                        
                        {/* HEADER ROW */}
                        <div className="lbr" style={{ fontSize: '11px', fontWeight: 700, color: 'var(--tlight)', textTransform: 'uppercase', letterSpacing: '.5px', display: 'flex', alignItems: 'center' }}>
                          <div className="lbrank" style={{ visibility: 'hidden', width: '32px' }}>#</div>
                          
                          {/* Force Employee header to match row space */}
                          <div className="lbi" style={{ flex: 1, minWidth: 0 }}>Employee</div> 
                          
                          {/* Quiz headers matching data columns exactly */}
                          <div style={{ display: 'flex', gap: '8px' }}>
                            {Array.from({ length: Math.min(MAX_QUIZ_WEEKS, data.week_count) }, (_, wi) => (
                              <span key={wi} style={{ width: '100px', textAlign: 'center', display: 'inline-block' }}>W{wi + 1} Quiz</span>
                            ))}
                          </div>
                          <div className="lbp" style={{ visibility: 'hidden', width: '50px', textAlign: 'right' }}>%</div>
                        </div>

                        {/* DATA ROWS */}
                        {sortedLeaderboardEmployees.slice(0, 20).map((item, idx) => (
                          <div className="lbr" key={item.employee.emp_code} style={{ display: 'flex', alignItems: 'center' }}>
                            <div className={`lbrank ${getRankClass(idx)}`} style={{ width: '32px' }}>{idx + 1}</div>
                            
                            {/* flex: 1 ensures it consumes remaining space evenly across all rows */}
                            <div className="lbi" style={{ flex: 1, minWidth: 0 }}>
                              <div className="lbn" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.employee.name}</div>
                              <div className="lbs">{item.employee.dept} &bull; {item.employee.location}</div>
                            </div>
                            
                            {/* Width increased to 100px to cleanly house "NOT APPEARED" */}
                            <div style={{ display: 'flex', gap: '8px' }}>
                              {item.employee.weekly.slice(0, MAX_QUIZ_WEEKS).map((wk, wi) => (
                                <span key={wi} style={{ width: '100px', display: 'inline-flex', justifyContent: 'center', alignItems: 'center' }}>
                                  {renderQuizBadge(wk.quiz)}
                                </span>
                              ))}
                            </div>
                            <div className="lbp" style={{ color: getPctColor(item.overall), width: '50px', textAlign: 'right' }}>{item.overall}%</div>
                          </div>
                        ))}

                      </div>
                    </div>
                  </div>
                  <div>
                    <div className="sh">Department Leaderboard</div>
                    <div className="ssub">Ranked by overall average %</div>
                    <div className="card" style={{ padding: 0 }}>
                      <div className="sy2" style={{ padding: '16px' }}>
                        {data.dept_overall.map((dep, idx) => (
                          <div className="lbr" key={dep.dept}>
                            <div className={`lbrank ${getRankClass(idx)}`}>{idx + 1}</div>
                            <div className="lbi">
                              <div className="lbn">{dep.dept}</div>
                              <div className="lbs">{dep.count} employees &bull; {dep.weekly_avg.map((v, i) => `W${i + 1}: ${v}%`).join(' ')}</div>
                              <div style={{ marginTop: '6px' }}>{renderProgressBar(dep.overall_avg)}</div>
                            </div>
                            <div className="lbp" style={{ color: getPctColor(dep.overall_avg) }}>{dep.overall_avg}%</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="sh">Quiz Performance &mdash; All Employees</div>
                <div className="tw">
                  <table>
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Employee</th>
                        <th>Department</th>
                        {data.emp_data.length > 0 && data.emp_data[0].weekly.map((_, wi) => (
                          <React.Fragment key={wi}>
                            {wi < MAX_QUIZ_WEEKS && <th>W{wi + 1} Quiz</th>}
                            <th>W{wi + 1} %</th>
                          </React.Fragment>
                        ))}
                        <th>Overall %</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedLeaderboardEmployees.slice(0, 20).map((item, idx) => (
                        <tr key={item.employee.emp_code}>
                          <td><div className={`lbrank ${getRankClass(idx)}`}>{idx + 1}</div></td>
                          <td><strong>{item.employee.name}</strong></td>
                          <td>{item.employee.dept}</td>
                          {item.employee.weekly.map((wk, wi) => (
                            <React.Fragment key={wi}>
                              {wi < MAX_QUIZ_WEEKS && <td>{renderQuizBadge(wk.quiz)}</td>}
                              <td>{renderPctText(wk.pct)}</td>
                            </React.Fragment>
                          ))}
                          <td>{renderPctText(item.overall)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* ── DEPARTMENTS PANEL ─────────────────────────────────────── */}
              <div className={`pnl ${activeTab === 'dp' ? 'on' : ''}`}>
                <div className="srch">
                  <input 
                    className="si" 
                    type="text" 
                    placeholder="Search department..." 
                    value={deptSearch}
                    onChange={(e) => setDeptSearch(e.target.value)}
                  />
                </div>
                <div className="g3">
                  {data.dept_overall
                    .filter(d => !deptSearch || d.dept.toLowerCase().includes(deptSearch.toLowerCase()))
                    .map(dep => {
                      return (
                        <div 
                          className="dc" 
                          style={{ borderTop: `4px solid ${getPctColor(dep.overall_avg)}` }} 
                          key={dep.dept}
                          onClick={() => {
                            setSelectedDeptDrill(dep.dept);
                            setTimeout(() => drilldownRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
                          }}
                        >
                          <div style={{ fontWeight: 700, fontSize: '13px', marginBottom: '8px' }}>{dep.dept}</div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                            <div>
                              <div style={{ fontSize: '24px', fontWeight: 800, color: getPctColor(dep.overall_avg) }}>{dep.overall_avg}%</div>
                              <div style={{ fontSize: '11px', color: 'var(--tlight)' }}>{dep.count} employees</div>
                            </div>
                            <div style={{ textAlign: 'right' }}>
                              {dep.weekly_avg.map((v, i) => (
                                <div style={{ fontSize: '11px', color: 'var(--tlight)' }} key={i}>W{i + 1}: <strong>{v}%</strong></div>
                              ))}
                            </div>
                          </div>
                          <div style={{ margin: '10px 0' }}>{renderProgressBar(dep.overall_avg)}</div>
                          <div>
                            {dep.weekly_quiz.slice(0, MAX_QUIZ_WEEKS).map((wq, wi) => (
                              <div style={{ marginBottom: '5px' }} key={wi}>
                                <div style={{ fontSize: '10px', fontWeight: 700, color: 'var(--tlight)', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: '3px' }}>
                                  Weekly Quiz {weeklyQuizLabel(wi)}
                                </div>
                                <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                                  <span className="bdg bp">Pass: {wq.pass}</span><span className="bdg bf">Fail: {wq.fail}</span><span className="bdg bn2">NA: {wq.na}</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                </div>

                {selectedDeptDrill && (
                  <div id="ddrill" ref={drilldownRef}>
                    <div className="sh">{selectedDeptDrill} - Employee Breakdown</div>
                    <div className="tw" style={{ marginBottom: '20px' }}>
                      <table>
                        <thead>
                          <tr>
                            <th>Employee</th>
                            <th>Emp Code</th>
                            {data.emp_data.length > 0 && data.emp_data[0].weekly.map((_, wi) => (
                              <React.Fragment key={wi}>
                                <th>W{wi + 1} %</th>
                                {wi < MAX_QUIZ_WEEKS && <th>W{wi + 1} Quiz</th>}
                              </React.Fragment>
                            ))}
                            <th>Overall %</th>
                          </tr>
                        </thead>
                        <tbody>
                          {data.emp_data
                            .filter(e => e.dept === selectedDeptDrill)
                            .sort((a, b) => (calculateOverallPct(b) || 0) - (calculateOverallPct(a) || 0))
                            .map(e => {
                              const o2 = calculateOverallPct(e);
                              return (
                                <tr key={e.emp_code}>
                                  <td>
                                    <strong>{e.name}</strong>
                                    <div style={{ fontSize: '11px', color: 'var(--tlight)' }}>{e.email}</div>
                                  </td>
                                  <td>{e.emp_code}</td>
                                  {e.weekly.map((wk, wi) => (
                                    <React.Fragment key={wi}>
                                      <td>{renderPctText(wk.pct)}</td>
                                      {wi < MAX_QUIZ_WEEKS && <td>{renderQuizBadge(wk.quiz)}</td>}
                                    </React.Fragment>
                                  ))}
                                  <td>{renderPctText(o2)}</td>
                                </tr>
                              );
                            })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>

              {/* ── CONTENT PANEL ─────────────────────────────────────────── */}
              <div className={`pnl ${activeTab === 'co' ? 'on' : ''}`}>
                <div className="srch">
                  <select className="ss" id="wkf" value={contentWeekFilter} onChange={(e) => setContentWeekFilter(parseInt(e.target.value, 10))}>
                    <option value="0">All Weeks</option>
                    {contentWeekOptions.map(w => <option value={w} key={w}>Week {w}</option>)}
                  </select>
                </div>
                <div id="clist">
                  {data.content_stats
                    .filter(c => !contentWeekFilter || c.week === contentWeekFilter)
                    .map(c => {
                      const lds = c.dept_stats || {};
                      const ldk = Object.keys(lds).sort((a, b) => lds[b].pct - lds[a].pct);
                      return (
                        <div style={{ background: '#fff', borderRadius: '10px', border: '1px solid var(--border)', overflow: 'hidden', marginBottom: '16px', boxShadow: 'var(--shadow)' }} key={c.col}>
                          <div className="cch">
                            <div>
                              <div style={{ fontWeight: 700, fontSize: '13px' }}>{c.title}</div>
                              <div style={{ fontSize: '11px', color: 'var(--gold2)' }}>Week {c.week} &bull; {c.date}</div>
                            </div>
                            <div style={{ textAlign: 'right' }}>
                              <div style={{ fontSize: '24px', fontWeight: 800, color: getPctColor(c.pct) }}>{c.pct}%</div>
                              <div style={{ fontSize: '11px', color: 'rgba(255,255,255,.6)' }}>{c.yes} / {c.total} consumed</div>
                            </div>
                          </div>
                          <div className="ccb">
                            <div style={{ marginBottom: '12px' }}>{renderProgressBar(c.pct)}</div>
                            <div style={{ fontSize: '12px', fontWeight: 700, color: 'var(--tmid)', marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '.5px' }}>Dept-wise Consumption</div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 24px' }}>
                              {ldk.map(deptKey => (
                                <div className="dr" key={deptKey}>
                                  <div className="dl" title={deptKey}>{deptKey}</div>
                                  <div style={{ flex: 1 }}>{renderProgressBar(lds[deptKey].pct)}</div>
                                  <div className="dv" style={{ color: getPctColor(lds[deptKey].pct) }}>{lds[deptKey].pct}%</div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                </div>
              </div>

              {/* ── EMPLOYEE TRACKER PANEL ─────────────────────────────────── */}
              <div className={`pnl ${activeTab === 'em' ? 'on' : ''}`}>
                <div className="srch">
                  <input 
                    className="si" 
                    type="text" 
                    placeholder="Search by name, email or emp code..." 
                    value={empSearchQuery}
                    onChange={(e) => setEmpSearchQuery(e.target.value)}
                  />
                  <select className="ss" value={empDeptFilter} onChange={(e) => setEmpDeptFilter(e.target.value)}>
                    <option value="">All Departments</option>
                    {uniqueDepartments.map(d => <option value={d} key={d}>{d}</option>)}
                  </select>
                </div>
                
                {filteredEmployeeSearchResults.length > 0 && (
                  <div className="card sy" style={{ padding: 0, marginBottom: '20px' }}>
                    <table>
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Emp Code</th>
                          <th>Department</th>
                          {data.emp_data.length > 0 && data.emp_data[0].weekly.map((_, wi) => (
                            <th key={wi}>W{wi + 1} %</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {filteredEmployeeSearchResults.map(e => (
                          <tr key={e.email || e.emp_code} onClick={() => setSelectedEmpEmail(e.email)} style={{ cursor: 'pointer' }}>
                            <td><strong>{e.name}</strong></td>
                            <td>{e.emp_code}</td>
                            <td>{e.dept}</td>
                            {e.weekly.map((wk, wi) => (
                              <td key={wi}>{renderPctText(wk.pct)}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {selectedEmployeeObject ? (
                  <div id="eprof">
                    <div className="ep">
                      {(() => {
                        const o2 = calculateOverallPct(selectedEmployeeObject);
                        return (
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px' }}>
                            <div>
                              <div className="epn">{selectedEmployeeObject.name}</div>
                              <div className="epm">{selectedEmployeeObject.emp_code} &bull; {selectedEmployeeObject.email}</div>
                              <div className="epm" style={{ marginTop: '4px' }}>{selectedEmployeeObject.dept} &bull; {selectedEmployeeObject.location}</div>
                            </div>
                            <div style={{ textAlign: 'right' }}>
                              <div style={{ fontSize: '36px', fontWeight: 800, color: getPctColor(o2 || 0) }}>{o2 !== null ? `${o2}%` : '—'}</div>
                              <div style={{ fontSize: '12px', color: 'rgba(255,255,255,.6)' }}>Overall</div>
                            </div>
                          </div>
                        );
                      })()}
                      <div
                        className="eps"
                        style={{
                          gridTemplateColumns: `repeat(${selectedEmployeeObject.weekly.length + Math.min(selectedEmployeeObject.weekly.length, MAX_QUIZ_WEEKS)}, 1fr)`
                        }}
                      >
                        {selectedEmployeeObject.weekly.map((wk, wi) => (
                          <React.Fragment key={wi}>
                            <div>
                              <div className="epv">{wk.pct !== null ? `${wk.pct}%` : '—'}</div>
                              <div className="epl">Week {wi + 1}</div>
                            </div>
                            {wi < MAX_QUIZ_WEEKS && (
                              <div>
                                <div className="epv">{wk.quiz || '—'}</div>
                                <div className="epl">W{wi + 1} Quiz</div>
                              </div>
                            )}
                          </React.Fragment>
                        ))}
                      </div>
                    </div>

                    <div className="g2">
                      <div>
                        <div className="sh">Weekly Progress</div>
                        <div className="card">
                          {selectedEmployeeObject.weekly.map((wk, wi) => (
                            <div style={{ marginBottom: wi === selectedEmployeeObject.weekly.length - 1 ? 0 : '18px' }} key={wi}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                                <span style={{ fontWeight: 600 }}>Week {wi + 1}</span>
                                <span style={{ fontWeight: 800, color: getPctColor(wk.pct) }}>
                                  {wk.pct !== null ? `${wk.pct}%` : '—'}
                                </span>
                              </div>
                              {wk.pct !== null ? renderProgressBar(wk.pct) : <div style={{ color: 'var(--tlight)', fontSize: '12px' }}>N/A</div>}
                            </div>
                          ))}
                        </div>
                      </div>
                      <div>
                        <div className="sh">Quiz Results</div>
                        <div className="card" id="epqz">
                          {selectedEmployeeObject.weekly.slice(0, MAX_QUIZ_WEEKS).map((wk, wi, arr) => (
                            <div
                              style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                padding: '14px 0',
                                borderBottom: wi === arr.length - 1 ? 'none' : '1px solid var(--border)'
                              }}
                              key={wi}
                            >
                              <span style={{ fontWeight: 600 }}>Week {wi + 1} Quiz</span>
                              {renderQuizBadge(wk.quiz)}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    <div className="sh">Content Consumption</div>
                    <div className="card">
                      {contentWeekOptions.map(w => {
                        const items = data.content_info.filter(ci => ci.week === w).map(ci => {
                          const val = selectedEmployeeObject.content[ci.col] || 'NA';
                          return { title: ci.title, date: ci.date, val };
                        });
                        if (!items.length) return null;
                        return (
                          <div style={{ marginBottom: '20px' }} key={w}>
                            <div style={{ fontSize: '12px', fontWeight: 700, color: 'var(--tmid)', textTransform: 'uppercase', letterSpacing: '.8px', marginBottom: '10px' }}>Week {w}</div>
                            {items.map((ci, idx) => {
                              const icon = ci.val === 'Yes' ? '✅' : ci.val === 'No' ? '❌' : '⏳';
                              const bc = ci.val === 'Yes' ? 'byes' : ci.val === 'No' ? 'bno' : 'bn2';
                              return (
                                <div className="cp" key={idx}>
                                  <span style={{ fontSize: '16px' }}>{icon}</span>
                                  <span style={{ flex: 1, fontSize: '12px', fontWeight: 600, color: 'var(--navy)' }}>{ci.title}</span>
                                  <span style={{ fontSize: '11px', color: 'var(--tlight)' }}>{ci.date}</span>
                                  <span className={`bdg ${bc}`}>{ci.val === 'NA' ? 'N/A' : ci.val}</span>
                                </div>
                              );
                            })}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  <div className="card" id="ephl" style={{ textAlign: 'center', padding: '48px' }}>
                    <div style={{ fontSize: '48px', marginBottom: '12px' }}>🔍</div>
                    <div style={{ fontSize: '16px', fontWeight: 700, marginBottom: '8px' }}>Search for an employee</div>
                    <div style={{ color: 'var(--tlight)' }}>Enter name, email or employee code</div>
                  </div>
                )}
              </div>
            </>
          ) : null}

          {/* ── UPLOAD DATA PANEL (Always rendered, condition controls metrics fallback) ── */}
          <div className={`pnl ${activeTab === 'up' ? 'on' : ''}`}>
            <div className="card" style={{ marginBottom: '20px' }}>
              <div className="sh">Upload Daily LMS Report</div>
              <p style={{ color: 'var(--tmid)', fontSize: '13px', marginBottom: '20px' }}>Upload the updated Excel file. All tabs will refresh automatically.</p>
              
              <div 
                className="upz" 
                style={{ backgroundColor: isDragging ? '#ffe8cc' : '' }}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
              >
                <div style={{ fontSize: '48px', marginBottom: '12px' }}>📋</div>
                <div style={{ fontSize: '16px', fontWeight: 700, marginBottom: '8px' }}>Drop Excel file here</div>
                <div style={{ fontSize: '13px', color: 'var(--tlight)' }}>or click to browse (.xlsx)</div>
                <button className="ubtn" onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}>Choose File</button>
              </div>
              
              <input 
                type="file" 
                ref={fileInputRef}
                accept=".xlsx,.xls" 
                style={{ display: 'none' }} 
                onChange={handleFileChange}
              />
              
              {uploadStatus.text && (
                <div className={uploadStatus.className || 'uok'} style={{ marginTop: '12px' }}>
                  {uploadStatus.text}
                </div>
              )}
              
              {diagnosticRows && (
                <details style={{ marginTop: '12px', fontSize: '11px', fontFamily: 'monospace', background: '#f5f5f5', padding: '10px', borderRadius: '6px' }}>
                  <summary style={{ cursor: 'pointer', fontWeight: 700 }}>Raw diagnostics (click)</summary>
                  <div><b>R0:</b> {diagnosticRows.r0}</div>
                  <div><b>R1:</b> {diagnosticRows.r1}</div>
                  <div><b>R2:</b> {diagnosticRows.r2}</div>
                </details>
              )}
            </div>
            
            <div className="card">
              <div className="sh">Expected File Format</div>
              <ul style={{ marginTop: '12px', paddingLeft: '20px', color: 'var(--tmid)', fontSize: '12px', lineHeight: 1.9 }}>
                <li>Row 1: blank</li>
                <li>Row 2: Content titles in cols 7+</li>
                <li>Row 3: Sr. No., Emp. code, Name, Email, Department, Reporting Manager, Location, then repeating groups of [dates], Week N Consumption (%), Weekly Quiz N &mdash; as many weeks as the file contains</li>
                <li>Row 4+: Yes/No/NA and Pass/Fail/Not Appeared</li>
                <li>Any number of weeks is supported &mdash; the dashboard detects every "Consumption (%)" column automatically</li>
              </ul>
            </div>
          </div>
        </div>
        <div className={`toast ${toastType} ${toastShow ? 'show' : ''}`} id="toast">
            {toastType === 'success' ? '✅' : toastType === 'error' ? '❌' : 'ℹ️'} {toastMessage}
        </div>
      </div>
    </>
  );
};

export default LmsConsumptionDashboard;
